import { useKeenSlider, KeenSliderPlugin } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import React, { useEffect, useState } from 'react';
import { DiagramArrow } from './DiagramArrow';
import { useResizeListener } from '../../hooks/windowEvents/useResizeListener';
import cc from 'classcat';

interface IDiagramSlider {
    children: React.ReactNode;
}

const ResizePlugin: KeenSliderPlugin = slider => {
    const observer = new ResizeObserver(function () {
        slider.update();
    });

    slider.on('created', () => {
        observer.observe(slider.container);
    });
    slider.on('destroyed', () => {
        observer.unobserve(slider.container);
    });
};

const DiagramSlider = ({ children }: IDiagramSlider) => {
    const [loaded, setLoaded] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const childrenCount = React.Children.count(children);
    const onlyOnePolicy = childrenCount === 1;
    const [perView, setPerView] = useState(1);

    const sliderOptions = {
        initial: 0,
        breakpoints: {
            '(max-width: 2560px)': {
                slides: { perView: onlyOnePolicy ? 1 : 2 },
            },
            '(max-width: 900px)': {
                slides: { perView: onlyOnePolicy ? 1 : 1.1 },
            },
        },
        slides: {
            perView: onlyOnePolicy ? 1 : 3,
        },
        slideChanged(slider: any) {
            setCurrentSlide(slider.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
    };

    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(sliderOptions, [ResizePlugin]);

    useEffect(() => {
        instanceRef?.current?.update();
    }, [children]);

    useResizeListener(innerWidth => {
        if (innerWidth <= 900) return setPerView(1);
        if (innerWidth <= 2560) return setPerView(2);
        setPerView(3);
    });

    const isLastSlide = currentSlide === childrenCount - 1;

    const isRightArrowDisabled = () => {
        if (onlyOnePolicy) return true;
        return currentSlide === childrenCount - perView;
    };

    return (
        <div className={cc(['diagram-slider', { 'diagram-slider--reverse-margin': isLastSlide }, { 'diagram-slider--one-slide': onlyOnePolicy }])}>
            <div ref={sliderRef} className="keen-slider">
                {children}
            </div>
            {loaded && instanceRef.current && perView >= 2 && (
                <>
                    <DiagramArrow
                        orientation="left"
                        disabled={currentSlide === 0}
                        onClick={e => {
                            e.stopPropagation();
                            instanceRef.current?.prev();
                        }}
                    />
                    <DiagramArrow
                        orientation="right"
                        disabled={isRightArrowDisabled()}
                        onClick={e => {
                            e.stopPropagation();
                            instanceRef.current?.next();
                        }}
                    />
                </>
            )}
        </div>
    );
};

export * from './DiagramSlide';

export default DiagramSlider;
